/**
 * Audit Trail Page - System Access Logs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Download, History, RefreshCw, ChevronRight, X, User, Shield,
  FileText, Activity, Loader2, ArrowUpDown 
} from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AuditTrailPage() {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    eventType: '',
    userEmail: '',
    days: 7,
  });
  const [selectedEvent, setSelectedEvent] = useState(null);

  const fetchAuditData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.set('category', filters.category);
      if (filters.eventType) params.set('event_type', filters.eventType);
      if (filters.userEmail) params.set('user_email', filters.userEmail);
      params.set('limit', '200');

      const [eventsRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/audit-trail/?${params}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/audit-trail/summary?days=${filters.days}`, { credentials: 'include' }),
      ]);

      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setEvents(eventsData.events || []);
      }

      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData);
      }
    } catch (error) {
      toast.error('Failed to load audit trail');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAuditData();
  }, [fetchAuditData]);

  const handleExport = async (format) => {
    try {
      const response = await fetch(
        `${API_URL}/api/audit-trail/export?format=${format}&days=${filters.days}`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        toast.error('Export failed');
        return;
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eyvo_audit_trail.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Exported audit trail as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed');
    }
  };

  const formatEventType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';
  };

  const getEventIcon = (eventType) => {
    if (eventType?.includes('login')) return <User size={14} />;
    if (eventType?.includes('control')) return <Shield size={14} />;
    if (eventType?.includes('policy')) return <FileText size={14} />;
    if (eventType?.includes('http_request')) return <Activity size={14} />;
    return <History size={14} />;
  };

  const getEventColor = (eventType, statusCode) => {
    if (statusCode >= 400) return '#dc2626';
    if (eventType?.includes('login')) return '#059669';
    if (eventType?.includes('update') || eventType?.includes('create')) return '#7c3aed';
    if (eventType?.includes('download') || eventType?.includes('export')) return '#0891b2';
    return '#6b7280';
  };

  return (
    <div className="audit-trail-page">
      <div className="page-header">
        <div>
          <h1>Audit Trail</h1>
          <p>System access logs and activity history for compliance tracking</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => handleExport('csv')} data-testid="export-csv-btn">
            <Download size={16} /> Export CSV
          </button>
          <button className="btn-secondary" onClick={() => handleExport('json')} data-testid="export-json-btn">
            <Download size={16} /> Export JSON
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="audit-summary-grid">
          <Card className="audit-stat">
            <div className="stat-value">{summary.total_events?.toLocaleString()}</div>
            <div className="stat-label">Total Events ({summary.period_days}d)</div>
          </Card>
          <Card className="audit-stat">
            <div className="stat-value">{summary.unique_users}</div>
            <div className="stat-label">Unique Users</div>
          </Card>
          <Card className="audit-stat">
            <div className="stat-value">{summary.login_events}</div>
            <div className="stat-label">Login Events</div>
          </Card>
          <Card className="audit-stat">
            <div className="stat-value">{summary.modifications}</div>
            <div className="stat-label">Modifications</div>
          </Card>
          <Card className="audit-stat alert">
            <div className="stat-value">{summary.failed_requests}</div>
            <div className="stat-label">Failed Requests</div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="audit-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Category</label>
            <select
              value={filters.category}
              onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              <option value="authentication">Authentication</option>
              <option value="access">Access</option>
              <option value="modifications">Modifications</option>
              <option value="administrative">Administrative</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Time Period</label>
            <select
              value={filters.days}
              onChange={(e) => setFilters(f => ({ ...f, days: parseInt(e.target.value) }))}
              data-testid="days-filter"
            >
              <option value="1">Last 24 hours</option>
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          <div className="filter-group">
            <label>User Email</label>
            <input
              type="text"
              placeholder="Filter by user..."
              value={filters.userEmail}
              onChange={(e) => setFilters(f => ({ ...f, userEmail: e.target.value }))}
              data-testid="user-filter"
            />
          </div>
          <button className="btn-secondary" onClick={fetchAuditData} data-testid="refresh-audit-btn">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </Card>

      {/* Events Table */}
      <Card className="audit-events-table">
        <div className="table-header">
          <h3>Activity Log</h3>
          <span className="event-count">{events.length} events</span>
        </div>
        {loading ? (
          <div className="loading-container"><Loader2 className="spin" size={32} /></div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <History size={48} color="#6b7280" />
            <p>No audit events found for the selected filters.</p>
          </div>
        ) : (
          <div className="events-list">
            {events.map((event, i) => (
              <div
                key={event._id || i}
                className="event-row"
                onClick={() => setSelectedEvent(event)}
                data-testid={`event-row-${i}`}
              >
                <div className="event-icon" style={{ color: getEventColor(event.event_type, event.status_code) }}>
                  {getEventIcon(event.event_type)}
                </div>
                <div className="event-info">
                  <div className="event-type">{formatEventType(event.event_type)}</div>
                  <div className="event-details">
                    {event.path && <span className="event-path">{event.method} {event.path}</span>}
                    {event.control_id && <span className="event-control">Control: {event.control_id}</span>}
                    {event.user_email && <span className="event-user">{event.user_email}</span>}
                  </div>
                </div>
                <div className="event-meta">
                  {event.status_code && (
                    <Badge color={event.status_code >= 400 ? '#dc2626' : '#059669'}>
                      {event.status_code}
                    </Badge>
                  )}
                  <span className="event-time">
                    {new Date(event.timestamp).toLocaleString()}
                  </span>
                </div>
                <ChevronRight size={16} className="event-arrow" />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="modal-content event-detail" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Event Details</h2>
              <button className="modal-close" onClick={() => setSelectedEvent(null)} data-testid="close-event-modal">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Event Type</label>
                  <span>{formatEventType(selectedEvent.event_type)}</span>
                </div>
                <div className="detail-item">
                  <label>Timestamp</label>
                  <span>{new Date(selectedEvent.timestamp).toLocaleString()}</span>
                </div>
                {selectedEvent.user_email && (
                  <div className="detail-item">
                    <label>User</label>
                    <span>{selectedEvent.user_email}</span>
                  </div>
                )}
                {selectedEvent.user_type && (
                  <div className="detail-item">
                    <label>User Type</label>
                    <span>{selectedEvent.user_type}</span>
                  </div>
                )}
                {selectedEvent.method && (
                  <div className="detail-item">
                    <label>Method</label>
                    <span>{selectedEvent.method}</span>
                  </div>
                )}
                {selectedEvent.path && (
                  <div className="detail-item full-width">
                    <label>Path</label>
                    <span className="code">{selectedEvent.path}</span>
                  </div>
                )}
                {selectedEvent.status_code && (
                  <div className="detail-item">
                    <label>Status Code</label>
                    <Badge color={selectedEvent.status_code >= 400 ? '#dc2626' : '#059669'}>
                      {selectedEvent.status_code}
                    </Badge>
                  </div>
                )}
                {selectedEvent.duration_ms !== undefined && (
                  <div className="detail-item">
                    <label>Duration</label>
                    <span>{selectedEvent.duration_ms}ms</span>
                  </div>
                )}
                {selectedEvent.client_ip && (
                  <div className="detail-item">
                    <label>Client IP</label>
                    <span>{selectedEvent.client_ip}</span>
                  </div>
                )}
                {selectedEvent.control_id && (
                  <div className="detail-item">
                    <label>Control ID</label>
                    <span>{selectedEvent.control_id}</span>
                  </div>
                )}
              </div>
              {selectedEvent.changes && Object.keys(selectedEvent.changes).length > 0 && (
                <div className="changes-section">
                  <h4>Changes Made</h4>
                  <div className="changes-list">
                    {Object.entries(selectedEvent.changes).map(([field, change]) => (
                      <div key={field} className="change-item">
                        <strong>{field}:</strong>
                        <span className="old-value">{String(change.old || '(empty)')}</span>
                        <ArrowUpDown size={14} />
                        <span className="new-value">{String(change.new || '(empty)')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selectedEvent.details && Object.keys(selectedEvent.details).length > 0 && (
                <div className="details-section">
                  <h4>Additional Details</h4>
                  <pre className="json-block">{JSON.stringify(selectedEvent.details, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
